/**
 * Business Glossary Catalog Component
 *
 * Displays business terms in a catalog layout with click-anywhere-to-edit cards
 * Data stored in E-Unify collections with full CRUD functionality.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  CircularProgress,
  Box,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon
} from '@mui/icons-material';
import BusinessTermCard from './BusinessTermCard';
import businessTermService, {
  BusinessTerm,
  BusinessTermCreateInput,
  BusinessTermUpdateInput,
  BusinessTermFilters
} from '../../services/businessTermService';

// Inline implementation of useDebounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface BusinessGlossaryCatalogProps {
  title?: string;
  subtitle?: string;
  compact?: boolean;
  showCreateButton?: boolean;
}

const BusinessGlossaryCatalog: React.FC<BusinessGlossaryCatalogProps> = ({
  title = "Business Glossary",
  subtitle = "Explore and manage business terminology with governance controls",
  compact = false,
  showCreateButton = true
}) => {
  // State management
  const [terms, setTerms] = useState<BusinessTerm[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [filters, setFilters] = useState<BusinessTermFilters>({
    limit: compact ? 20 : 50
  });

  // Edit state
  const [editingTermId, setEditingTermId] = useState<string | null>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTerm, setNewTerm] = useState<BusinessTermCreateInput>({
    name: '',
    definition: '',
    category: 'Business Term'
  });
  const [creating, setCreating] = useState(false);

  // Notifications
  const [saveSuccess, setSaveSuccess] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');

  // Debounced search text
  const debouncedSearchText = useDebounce(searchText, 500);

  // Fetch terms function
  const fetchTerms = useCallback(async () => {
    if (terms.length === 0) {
      setLoading(true);
    }
    setError(null);

    try {
      const searchFilters: BusinessTermFilters = {
        ...filters,
        search: debouncedSearchText || undefined
      };

      const response = await businessTermService.getBusinessTerms(searchFilters);
      setTerms(response.data);
    } catch (err) {
      console.error('Failed to fetch business terms:', err);
      setError('Failed to load business glossary. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchText, filters]);

  // Fetch terms when search or filters change
  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  // Handle term edit
  const handleTermEdit = async (id: string, data: BusinessTermUpdateInput) => {
    try {
      await businessTermService.updateBusinessTerm(id, data);
      setSaveSuccess('Business term updated successfully');
      setEditingTermId(null);
      // Refresh terms
      fetchTerms();
    } catch (err) {
      console.error('Failed to update term:', err);
      setSaveError('Failed to update business term. Please try again.');
    }
  };

  // Handle term creation
  const handleTermCreate = async () => {
    if (!newTerm.name.trim() || !newTerm.definition.trim()) {
      setSaveError('Name and definition are required');
      return;
    }

    setCreating(true);
    try {
      await businessTermService.createBusinessTerm(newTerm);
      setSaveSuccess('Business term created successfully');
      setCreateDialogOpen(false);
      setNewTerm({
        name: '',
        definition: '',
        category: 'Business Term'
      });
      // Refresh terms
      fetchTerms();
    } catch (err) {
      console.error('Failed to create term:', err);
      setSaveError('Failed to create business term. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof BusinessTermFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setFilters({ limit: compact ? 20 : 50 });
  };

  return (
    <Container sx={{ py: compact ? 2 : 4 }} className="business-glossary-catalog">
      <Typography
        variant={compact ? "h5" : "h4"}
        component="h1"
        gutterBottom
        sx={{ color: '#003366', fontWeight: 700 }}
        tabIndex={-1}
      >
        {title}
      </Typography>
      {!compact && (
        <Typography variant="body1" paragraph>
          {subtitle}
        </Typography>
      )}

      {/* Search and Filters */}
      <Box sx={{ mb: compact ? 2 : 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={compact ? 12 : 6}>
            <TextField
              fullWidth
              variant="outlined"
              size={compact ? "small" : "medium"}
              placeholder="Search business terms..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      onClick={() => setSearchText('')}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              aria-label="Search business terms"
            />
          </Grid>

          {!compact && (
            <>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="pending-review">Pending Review</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category || ''}
                    label="Category"
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="Business Term">Business Term</MenuItem>
                    <MenuItem value="Data Element">Data Element</MenuItem>
                    <MenuItem value="Reference Data">Reference Data</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  fullWidth
                  size="small"
                  disabled={!searchText && !filters.status && !filters.category}
                >
                  Clear Filters
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: compact ? 2 : 4 }} aria-live="polite">
          <CircularProgress aria-label="Loading business glossary" />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Box sx={{ mt: 2, mb: 2 }} aria-live="assertive">
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Results Section */}
      {!loading && !error && (
        <>
          <Box sx={{ mb: compact ? 1 : 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} aria-live="polite">
            <Typography variant="subtitle1">
              {terms.length > 0
                ? `Showing ${terms.length} business terms`
                : 'No business terms found'}
            </Typography>

            {/* Active filters */}
            {(searchText || filters.status || filters.category) && !compact && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {searchText && (
                  <Chip
                    label={`Search: "${searchText}"`}
                    size="small"
                    onDelete={() => setSearchText('')}
                  />
                )}
                {filters.status && (
                  <Chip
                    label={`Status: ${filters.status}`}
                    size="small"
                    onDelete={() => handleFilterChange('status', '')}
                  />
                )}
                {filters.category && (
                  <Chip
                    label={`Category: ${filters.category}`}
                    size="small"
                    onDelete={() => handleFilterChange('category', '')}
                  />
                )}
              </Box>
            )}
          </Box>

          <Grid container spacing={compact ? 2 : 3}>
            {terms.map((term) => (
              <Grid item xs={12} sm={compact ? 12 : 6} md={compact ? 12 : 4} key={term._id}>
                <BusinessTermCard
                  term={term}
                  onEdit={handleTermEdit}
                  isEditing={editingTermId === term._id}
                  onEditStart={setEditingTermId}
                  onEditCancel={() => setEditingTermId(null)}
                  compact={compact}
                />
              </Grid>
            ))}
          </Grid>

          {/* Empty state */}
          {terms.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', mt: compact ? 4 : 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No business terms found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchText || filters.status || filters.category
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first business term'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Floating Action Button for Creating New Terms */}
      {showCreateButton && (
        <Fab
          color="primary"
          aria-label="Add new business term"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Create Term Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="create-term-dialog-title"
      >
        <DialogTitle id="create-term-dialog-title">
          Create New Business Term
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Term Name"
                value={newTerm.name}
                onChange={(e) => setNewTerm((prev: BusinessTermCreateInput) => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Definition"
                value={newTerm.definition}
                onChange={(e) => setNewTerm((prev: BusinessTermCreateInput) => ({ ...prev, definition: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Category"
                value={newTerm.category}
                onChange={(e) => setNewTerm((prev: BusinessTermCreateInput) => ({ ...prev, category: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Acronym (optional)"
                value={newTerm.acronym || ''}
                onChange={(e) => setNewTerm((prev: BusinessTermCreateInput) => ({ ...prev, acronym: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleTermCreate}
            variant="contained"
            disabled={creating || !newTerm.name.trim() || !newTerm.definition.trim()}
          >
            {creating ? 'Creating...' : 'Create Term'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess('')}
        message={saveSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Error Snackbar */}
      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BusinessGlossaryCatalog;
